import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock, X } from 'lucide-react';
import { reviewDaemonsFindingMutation } from '../../../generated/api/@tanstack/react-query.gen.js';
import {
  Badge,
  Button,
  Card,
  Chip,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FilterPills,
  QueryBoundary,
  Sheet,
  Toolbar,
  useToast,
} from '../../../components/ui/index.js';
import { Can } from '../../../components/auth/Can.js';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonKeys, daemonReads } from './daemon-queries.js';
import type { DaemonFindingRecord, DaemonRecord, FindingReviewUpdate } from '../../../generated/api/types.gen.js';

interface DaemonFindingsTabProps {
  daemon: DaemonRecord;
}

type Severity = NonNullable<Parameters<ReturnType<typeof daemonReads>['findings']>[1]>['severity'];
type ReviewStatus = NonNullable<Parameters<ReturnType<typeof daemonReads>['findings']>[1]>['reviewStatus'];

const SEVERITY_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  info: 'info',
  low: 'neutral',
  medium: 'warning',
  high: 'danger',
  critical: 'danger',
};

const REVIEW_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  deferred: 'neutral',
};

const SEVERITY_OPTIONS = [
  { id: 'all', label: 'All severities' },
  { id: 'critical', label: 'Critical' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
  { id: 'info', label: 'Info' },
];

const REVIEW_OPTIONS = [
  { id: 'all', label: 'All reviews' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'deferred', label: 'Deferred' },
];

const sentence = (value: string | null | undefined) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '—');

/**
 * `evidence` is an open JSONB blob in the API contract (`z.unknown()`), so it must be narrowed
 * before it can be rendered or measured.
 */
function isNonEmptyRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0;
}

function taskLink(daemonId: number, finding: DaemonFindingRecord): string {
  const params = new URLSearchParams({
    originType: 'daemon_finding',
    originId: String(finding.id),
    originLabel: finding.title ?? 'Daemon finding',
    title: `Resolve ${finding.title ?? 'daemon finding'}`,
    goal: finding.recommendation ?? finding.description ?? 'Investigate and resolve this daemon finding.',
    inputContext: JSON.stringify({
      daemonId,
      findingId: finding.id,
      severity: finding.severity,
      category: finding.category,
      evidence: finding.evidence ?? null,
    }),
  });
  return `/tasks/new?${params.toString()}`;
}

const COLUMNS: DataTableColumn<DaemonFindingRecord>[] = [
  {
    key: 'severity',
    header: 'Severity',
    render: (f) => (
      <Badge variant={SEVERITY_VARIANTS[f.severity ?? ''] ?? 'neutral'} size="sm">
        {sentence(f.severity)}
      </Badge>
    ),
  },
  { key: 'category', header: 'Category', render: (f) => <Chip>{f.category}</Chip> },
  { key: 'title', header: 'Finding', render: (f) => <span className="text-body">{f.title}</span> },
  {
    key: 'review',
    header: 'Review',
    render: (f) => (
      <Badge variant={REVIEW_VARIANTS[f.reviewStatus ?? ''] ?? 'neutral'} size="sm">
        {sentence(f.reviewStatus)}
      </Badge>
    ),
  },
  {
    key: 'date',
    header: 'Found',
    align: 'right',
    render: (f) => (f.createdAt ? new Date(f.createdAt).toLocaleDateString() : '—'),
  },
];

export const DaemonFindingsTab: React.FC<DaemonFindingsTabProps> = ({ daemon }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { scope, policy } = useDaemonScope();
  const [openId, setOpenId] = useState<number | null>(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [reviewFilter, setReviewFilter] = useState('all');

  const filters = {
    ...(severityFilter !== 'all' && { severity: severityFilter as Severity }),
    ...(reviewFilter !== 'all' && { reviewStatus: reviewFilter as ReviewStatus }),
  };
  const query = useQuery(daemonReads(scope).findings(daemon.id, Object.keys(filters).length > 0 ? filters : undefined));

  const review = useMutation({
    ...reviewDaemonsFindingMutation(),
    onSuccess: async () => {
      notify('Review saved');
      await queryClient.invalidateQueries({ queryKey: daemonKeys(scope).findings(daemon.id) });
    },
    onError: () => notify('Could not save the review', 'danger'),
  });

  const decide = (id: number, reviewStatus: FindingReviewUpdate['reviewStatus']) =>
    review.mutate({ path: { id: String(id) }, body: { reviewStatus } });

  return (
    <Card>
      <Toolbar
        pills={
          <>
            <FilterPills
              options={SEVERITY_OPTIONS}
              value={severityFilter}
              onChange={setSeverityFilter}
              aria-label="Severity"
            />
            <FilterPills options={REVIEW_OPTIONS} value={reviewFilter} onChange={setReviewFilter} aria-label="Review" />
          </>
        }
      />
      <QueryBoundary query={query} label="daemon findings" compact>
        {(data) => {
          const findings: DaemonFindingRecord[] = data.data ?? [];
          const open = findings.find((f) => f.id === openId) ?? null;
          return (
            <>
              <DataTable
                aria-label="Findings"
                columns={COLUMNS}
                rows={findings}
                rowKey={(f) => f.id}
                onRowClick={(f) => setOpenId(f.id)}
                pageSize={20}
                empty={
                  <EmptyState icon={Check} title="No findings" description="No findings match the current filters." />
                }
              />
              <Sheet
                open={open !== null}
                onClose={() => setOpenId(null)}
                title={open?.title ?? 'Finding'}
                width="lg"
                footer={
                  open ? (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Link
                        to={taskLink(daemon.id, open)}
                        className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
                      >
                        Create task
                      </Link>
                      {open.reviewStatus === 'pending' && (
                        <Can policy={policy}>
                          <Button
                            size="sm"
                            variant="success"
                            loading={review.isPending}
                            onClick={() => decide(open.id, 'approved')}
                          >
                            <Check size={12} /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            loading={review.isPending}
                            onClick={() => decide(open.id, 'rejected')}
                          >
                            <X size={12} /> Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={review.isPending}
                            onClick={() => decide(open.id, 'deferred')}
                          >
                            <Clock size={12} /> Defer
                          </Button>
                        </Can>
                      )}
                    </div>
                  ) : undefined
                }
              >
                {open && (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={SEVERITY_VARIANTS[open.severity ?? ''] ?? 'neutral'} size="sm">
                        {sentence(open.severity)}
                      </Badge>
                      <Badge variant={REVIEW_VARIANTS[open.reviewStatus ?? ''] ?? 'neutral'} size="sm">
                        {sentence(open.reviewStatus)}
                      </Badge>
                      <Chip>{open.category}</Chip>
                    </div>
                    {open.description && <p className="text-sm text-body">{open.description}</p>}
                    {open.recommendation && (
                      <div className="text-sm">
                        <span className="font-medium text-heading">Recommendation</span>
                        <p className="mt-1 text-body">{open.recommendation}</p>
                      </div>
                    )}
                    {isNonEmptyRecord(open.evidence) && (
                      <div>
                        <span className="text-xs font-medium text-muted">Evidence</span>
                        <pre className="mt-1 overflow-x-auto rounded-sm bg-surface-alt p-2 text-xs text-body">
                          {JSON.stringify(open.evidence, null, 2)}
                        </pre>
                      </div>
                    )}
                    {open.affectedFiles && open.affectedFiles.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-muted">Affected files</span>
                        <div className="mt-1 space-y-0.5">
                          {open.affectedFiles.map((file) => (
                            <code key={file} className="block text-xs text-body">
                              {file}
                            </code>
                          ))}
                        </div>
                      </div>
                    )}
                    {open.reviewedBy && (
                      <div className="text-xs text-muted">
                        Reviewed by {open.reviewedBy} on{' '}
                        {open.reviewedAt ? new Date(open.reviewedAt).toLocaleString() : '—'}
                      </div>
                    )}
                  </div>
                )}
              </Sheet>
            </>
          );
        }}
      </QueryBoundary>
    </Card>
  );
};
